import React, { useCallback, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Table as AntTable, Select, Button, Modal as AntModal } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import useAction from 'utils/useAction'
import Modal from 'components/Modal/Modal'
import {
  selectors as surpriseBoxSelectors,
  actions as surpriseBoxActions,
} from 'domains/surpriseBox'
import { getColumns } from './columns'
import {
  FILTER_STATUS_OPTIONS,
  FORM_MODAL_ID,
  DETAILS_MODAL_ID,
} from './constants'
import {
  Wrapper,
  PageHeader,
  FiltersRow,
  TableWrapper,
} from './SurpriseBox.styles'

const SurpriseBoxList = () => {
  const modal = useContext(Modal.Context)
  const data = useSelector(surpriseBoxSelectors.getData)
  const isLoaded = useSelector(surpriseBoxSelectors.getIsLoaded)
  const page = useSelector(surpriseBoxSelectors.getPage)
  const perPage = useSelector(surpriseBoxSelectors.getPerPage)
  const total = useSelector(surpriseBoxSelectors.getTotal)
  const status = useSelector(surpriseBoxSelectors.getStatus)

  const fetchPromotions = useAction(surpriseBoxActions.fetchPromotions)
  const activatePromotion = useAction(surpriseBoxActions.activatePromotion)
  const cancelPromotion = useAction(surpriseBoxActions.cancelPromotion)
  const clearData = useAction(surpriseBoxActions.clearData)

  useEffect(() => {
    fetchPromotions({ page: 1, status: '' })
    return () => clearData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = useCallback(
    (value) => {
      fetchPromotions({ page: 1, status: value || '' })
    },
    [fetchPromotions]
  )

  const handleTableChange = useCallback(
    (pagination) => {
      fetchPromotions({ page: pagination.current, status })
    },
    [fetchPromotions, status]
  )

  const handleCreate = useCallback(() => {
    modal.open(FORM_MODAL_ID, {})
  }, [modal])

  const handleView = useCallback(
    (record) => {
      modal.open(DETAILS_MODAL_ID, { id: record.id })
    },
    [modal]
  )

  const handleEdit = useCallback(
    (record) => {
      modal.open(FORM_MODAL_ID, { id: record.id })
    },
    [modal]
  )

  const handleActivate = useCallback(
    (record) => {
      AntModal.confirm({
        title: 'Activate promotion',
        content: `Activate "${record.name}"? Only one promotion can be active at a time.`,
        okText: 'Activate',
        onOk() {
          activatePromotion({ id: record.id })
        },
      })
    },
    [activatePromotion]
  )

  const handleCancel = useCallback(
    (record) => {
      AntModal.confirm({
        title: 'Cancel promotion',
        content: `Cancel "${record.name}"? This cannot be undone. The campaign will be archived and no further actions will be possible.`,
        okText: 'Cancel promotion',
        okType: 'danger',
        cancelText: 'Back',
        onOk() {
          cancelPromotion({ id: record.id })
        },
      })
    },
    [cancelPromotion]
  )

  const columns = getColumns({
    onView: handleView,
    onEdit: handleEdit,
    onActivate: handleActivate,
    onCancel: handleCancel,
  })

  return (
    <Wrapper>
      <PageHeader>
        <h2>Surprise Box Promotions</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          New Promotion
        </Button>
      </PageHeader>

      <FiltersRow>
        <Select
          placeholder="Status"
          style={{ width: 200 }}
          options={FILTER_STATUS_OPTIONS}
          onChange={handleStatusChange}
          allowClear
          value={status || undefined}
        />
      </FiltersRow>

      <TableWrapper>
        <AntTable
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={!isLoaded}
          onChange={handleTableChange}
          scroll={{ x: 1600 }}
          pagination={{
            current: page,
            pageSize: perPage,
            total,
            showSizeChanger: false,
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} promotions`,
          }}
        />
      </TableWrapper>
    </Wrapper>
  )
}

export default SurpriseBoxList
